import { motion } from 'motion/react'
import { mq, pxToRem, sizes } from 'src/theme'
import styled from 'styled-components'

import ActionButton from '../ActionButton'
import Picture from '../Picture'
import RichText from '../RichText'

import { SmallPushVariant } from './types'

type CommonProps = {
  $variant?: SmallPushVariant
}
type ContainerProps = CommonProps & {
  $color?: string
}

export const Container = styled(motion.section)<ContainerProps>`
  --color__block: ${({ $color, theme }) => $color ?? theme.colors.blueLight};
  display: flex;
  flex-flow: column;
  width: 100%;
  max-width: calc(100% - ${pxToRem(16)} * 2);
  margin-inline: auto;
  background-color: var(--color__block);
  &:not(:first-child) {
    margin-block-start: var(--g_spacing__blocks);
  }
  @media ${mq(sizes.desktop)} {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
  @media ${mq(sizes.big)} {
    grid-template-columns: ${({ $variant }) =>
      $variant === SmallPushVariant.imageLast
        ? 'calc(100%/3) 15% 1fr'
        : '1fr 15% calc(100%/3)'};
  }
`
export const Content = styled.div<CommonProps>`
  --padding-inline: ${pxToRem(24)};
  position: relative;
  z-index: 3;
  display: flex;
  flex-flow: column;
  gap: ${pxToRem(32)};
  padding-block: ${pxToRem(32)} ${pxToRem(40)};
  padding-inline: var(--padding-inline);
  @media ${mq(sizes.desktop)} {
    justify-content: center;
    grid-row: 1 / span 1;
    grid-column: ${({ $variant }) =>
      $variant === SmallPushVariant.imageLast ? '1 / span 1' : '2 / span 1'};
  }
  @media ${mq(sizes.big)} {
    --padding-inline: ${({ $variant }) =>
      $variant === SmallPushVariant.imageLast
        ? `${pxToRem(40)} ${pxToRem(56)}`
        : `${pxToRem(56)} ${pxToRem(40)}`};
    grid-column: ${({ $variant }) =>
      $variant === SmallPushVariant.imageLast ? '1 / span 1' : '3 / span 1'};
  }
`
export const Title = styled.h2`
  ${({ theme }) => theme.textStyles.m375_h2}
  @media ${mq(sizes.desktop)} {
    ${({ theme }) => theme.textStyles.d1440_h2}
  }
`
export const _RichText = styled(RichText)`
  ${({ theme }) => theme.textStyles.m375_body}
  @media ${mq(sizes.desktop)} {
    ${({ theme }) => theme.textStyles.d1440_body}
  }
`
export const _ActionButton = styled(ActionButton)`
  &:not(:first-child) {
    margin-block-start: ${pxToRem(8)};
  }
`
export const _Picture = styled(Picture)<CommonProps>`
  position: relative;
  z-index: 1;
  overflow: hidden;
  @media ${mq(sizes.desktop)} {
    grid-column: ${({ $variant }) =>
      $variant === SmallPushVariant.imageLast ? '2 / span 1' : '1 / span 1'};
    grid-row: 1 / span 1;
    align-self: stretch;
    & img {
      object-fit: cover;
      transition: scale 0.3s ease-in-out;
    }
    ${Container}:has(a:hover, a:focus, button:not(:disabled):hover, button:not(:disabled):focus) & img {
      scale: 1.05;
    }
  }
  @media ${mq(sizes.big)} {
    grid-column: ${({ $variant }) =>
      $variant === SmallPushVariant.imageLast ? '2 / span 2' : '1 / span 2'};
  }
`
export const Decoration = styled.div<CommonProps>`
  display: none;
  pointer-events: none;
  @media ${mq(sizes.big)} {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: flex-end;
    grid-column: ${({ $variant }) =>
      $variant === SmallPushVariant.imageLast ? '1 / span 2' : '2 / span 2'};
    grid-row: 1 / span 1;
  }
`
export const Shape = styled.div`
  height: 12%;
  width: 100%;
  background: var(--color__block);
  transition: scale 0.3s ease-in-out;
  ${Container}:has(a:hover, a:focus, button:not(:disabled):hover, button:not(:disabled):focus) & {
    scale: 0.9 1;
  }
`
